import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useId, useRef, useState } from "react";
import { z } from "zod";
import { appCount } from "../src/app-dates.ts";
import {
  DraftingLevelSchema,
  ScopedDraftingSchema,
  WorkspaceDraftingSchema,
} from "../src/drafting-levels.ts";
import { refreshAgentReads } from "./agent/agent-reads.ts";
import { draftingKey, useCampaigns, useDraftingOverview } from "./campaign-data.ts";
import { LateReplySettings } from "./late-reply.tsx";
import { campaignsPath, useWorkspace, workspaceApi } from "./workspace-context.tsx";

/*
 * Drafting switches (docs/2026-09-19-drafting-toggles-research.md, UI row). A flip takes effect at
 * once, with no save step. Off anywhere wins, so every control also says which level currently
 * holds drafting, and only turning a whole workspace on asks first. Every flip goes through the
 * workspace on screen, and the server refuses a campaign or lead of any other.
 */

const StoredSchema = z
  .object({ state: ScopedDraftingSchema, updatedBy: z.string(), updatedAt: z.string() })
  .nullable();
export const DraftingDecisionSchema = z
  .object({
    // A server before LEA-476 has no flag, so drafting was available wherever it answered.
    available: z.boolean().default(true),
    workspace: z.object({ state: WorkspaceDraftingSchema, stored: StoredSchema }),
    campaign: z.object({ state: ScopedDraftingSchema, stored: StoredSchema }),
    lead: z.object({ state: ScopedDraftingSchema, stored: StoredSchema }).nullable(),
    heldBy: DraftingLevelSchema.nullable(),
  })
  .nullable();
export type DraftingDecision = z.infer<typeof DraftingDecisionSchema>;

type Flip =
  | { level: "workspace"; state: "on" | "off" }
  | { level: "campaign"; campaignId: string; state: z.infer<typeof ScopedDraftingSchema> }
  | { level: "lead"; planId: string; state: z.infer<typeof ScopedDraftingSchema> };

/*
 * Drafting held by the developer flag (`drafting_available`, LEA-476): no switch here can change it,
 * so the copy never points at one. Said wherever a switch would otherwise be offered.
 */
const NOT_AVAILABLE =
  "Drafting is not turned on for this workspace yet. LeadCue's team turns it on; until then its replies are imported and shown, and nothing is drafted.";

const FLIP_REFUSALS: Readonly<Record<string, string>> = {
  drafting_not_available: NOT_AVAILABLE,
  campaign_setup_incomplete:
    "This campaign starts drafting when its setup is finished, not before.",
  campaign_first_draft_held:
    "This campaign is waiting for its email copy to be checked. It starts drafting once the copy agrees with its settings or you confirm it on the campaign's page.",
  target_not_found: "That campaign is no longer in this workspace. Reload to see it as it is now.",
};

async function flip(workspaceId: string, body: Flip): Promise<void> {
  const response = await fetch(`${workspaceApi(workspaceId)}/drafting`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (response.ok) return;
  if (response.status === 401) throw new Error("Please sign in again.");
  /*
   * A refusal the server will give again on every retry says why, rather than "try again": a
   * campaign still in setup starts drafting when its setup finishes, not on a second press.
   */
  const answer: unknown = await response.json().catch(() => null);
  const code =
    answer !== null && typeof answer === "object" && "error" in answer ? String(answer.error) : "";
  const known = Object.hasOwn(FLIP_REFUSALS, code) ? FLIP_REFUSALS[code] : undefined;
  throw new Error(known ?? "The switch did not change. Try again in a moment.");
}

/**
 * A switch built from native radios. Tab reaches the group and leaves it; the arrow keys move
 * between its options; a click, Space or Enter chooses one.
 *
 * A browser selects each radio the arrow keys move to, and every switch built on this saves what
 * it is given, so choosing "Full signature" from "Best" saved "None" on the way, and passing Off on
 * a drafting switch turned drafting off (critique, 2026-09-25). Arrows now move the focus only.
 * While a choice saves, the group says it is busy rather than disabling itself, so the keyboard
 * keeps its place instead of falling back to the page.
 */
export function Segmented<S extends string>({
  label,
  value,
  options,
  disabled,
  onChange,
  className,
}: {
  label: string;
  value: S;
  options: readonly { value: S; label: string; unavailable?: boolean }[];
  disabled?: boolean;
  onChange: (value: S) => void;
  /** A variant of the switch's look, such as the composer's quiet one. */
  className?: string;
}) {
  const name = useId();
  const arrowed = useRef(false);
  const choose = (next: S): void => {
    if (disabled || next === value) return;
    onChange(next);
  };
  return (
    <fieldset
      className={className ? `lcr-segment ${className}` : "lcr-segment"}
      aria-disabled={disabled || undefined}
      aria-busy={disabled || undefined}
      onKeyDown={(event) => {
        arrowed.current = event.key.startsWith("Arrow");
      }}
      onPointerDown={() => {
        arrowed.current = false;
      }}
    >
      <legend className="lcr-drafting-legend">{label}</legend>
      {options.map((option) => (
        <label
          key={option.value}
          data-value={option.value}
          data-unavailable={option.unavailable ? "true" : undefined}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={option.unavailable && value !== option.value}
            onChange={() => {
              if (arrowed.current) {
                arrowed.current = false;
                return;
              }
              choose(option.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              event.preventDefault();
              choose(option.value);
            }}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}

const onOff = [
  { value: "on", label: "On" },
  { value: "off", label: "Off" },
] as const;
function scopedOptions(inherited: "on" | "off", parent: string) {
  return [
    { value: "on", label: "On" },
    { value: "off", label: "Off" },
    { value: "inherit", label: `Same as ${parent} (${inherited})` },
  ] as const;
}

/** In the switch's place while the developer flag holds drafting: its state, and who changes it. */
function HeldByFlag() {
  return (
    <div className="lcr-drafting-held" role="status">
      <span className="lcr-state lcr-state-compact" data-tone="off">
        <span className="lcr-state-dot" aria-hidden="true" />
        Not turned on yet
      </span>
      <p className="lcr-settings-note lcr-drafting-held-note">{NOT_AVAILABLE}</p>
    </div>
  );
}

/** The whole workspace's switch. */
function WorkspaceSwitch(props: {
  readonly name: string;
  readonly state: "on" | "off";
  readonly busy: boolean;
  readonly onChange: (state: "on" | "off") => void;
}) {
  return (
    <div className="lcr-drafting-row lcr-drafting-workspace">
      <span className="lcr-drafting-name">
        Whole workspace
        <small>Every campaign in {props.name}</small>
      </span>
      <Segmented
        label={`Drafting for workspace ${props.name}`}
        value={props.state}
        options={onOff}
        disabled={props.busy}
        onChange={props.onChange}
      />
    </div>
  );
}

/**
 * The workspace's own switch, for its settings page. Each campaign's switch is on that campaign's
 * page, next to the reasons it may be held; this section says how many are drafting and links to the
 * ones that are not, so a switched-off workspace and 20 switched-off campaigns are one click apart.
 */
export function DraftingSection({ onNavigate }: { readonly onNavigate: (href: string) => void }) {
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const overview = useDraftingOverview(workspace.id);
  const save = async (body: Flip): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await flip(workspace.id, body);
      setConfirming(false);
      await Promise.all([
        cache.invalidateQueries({ queryKey: draftingKey(workspace.id) }),
        cache.invalidateQueries({ queryKey: ["plan", workspace.id] }),
      ]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The switch did not change.");
      // A refusal can mean the workspace changed under the page (its flag cleared): read it again.
      void cache.invalidateQueries({ queryKey: draftingKey(workspace.id) });
    } finally {
      setBusy(false);
    }
  };
  const data = overview.data;
  /*
   * Counted exactly as the campaigns list counts, so the number here is the number on its "Not
   * drafting" tab. The switch read lists enabled campaigns only, and counting from it said 18 where
   * the list said 19.
   */
  const campaigns = useCampaigns(workspace.id);
  const rows = campaigns.status === "ready" ? campaigns.rows : null;
  const drafting = rows?.filter((row) => row.state === "drafting").length ?? 0;
  const off = (rows?.length ?? 0) - drafting;
  const offHref = campaignsPath(workspace.id, "?show=not_drafting");
  return (
    <section className="lcr-settings-section lcr-drafting" aria-labelledby="lcr-settings-title">
      <h1 id="lcr-settings-title" className="lcr-page-title">
        Drafting
      </h1>
      {data?.available !== false && (
        <p className="lcr-settings-note">
          Off anywhere wins: a campaign drafts only while this workspace is on. Replies are still
          imported and shown either way.
        </p>
      )}
      {overview.isPending && <p className="lcr-settings-note">Loading the switch…</p>}
      {overview.isError && (
        <p className="lcr-settings-error" role="alert">
          {overview.error.message}{" "}
          <button type="button" className="lcr-linklike" onClick={() => void overview.refetch()}>
            Try again
          </button>
        </p>
      )}
      {data && !data.available && <HeldByFlag />}
      {data?.available && (
        <div className="lcr-drafting-group">
          <WorkspaceSwitch
            name={data.name}
            state={data.state}
            busy={busy}
            onChange={(state) =>
              state === "on" ? setConfirming(true) : void save({ level: "workspace", state })
            }
          />
          {confirming && (
            <fieldset className="lcr-drafting-confirm">
              <legend className="lcr-drafting-legend">Confirm turning drafting on</legend>
              <p>
                Turn drafting on for {data.name}? Every campaign here that is on, or uses the
                workspace, starts drafting new replies, and held ones are released within the
                backfill limits.
              </p>
              <div className="lcr-actions">
                <button
                  type="button"
                  className="lcr-primary lcr-small-button"
                  disabled={busy}
                  onClick={() => void save({ level: "workspace", state: "on" })}
                >
                  Turn on
                </button>
                <button
                  type="button"
                  className="lcr-small-button"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                >
                  Keep off
                </button>
              </div>
            </fieldset>
          )}
          {rows !== null && (
            <p className="lcr-settings-note lcr-drafting-summary">
              {`${appCount(drafting)} of ${appCount(rows.length)} campaigns drafting. `}
              {off > 0 && (
                <a
                  href={offHref}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0)
                      return;
                    event.preventDefault();
                    onNavigate(offHref);
                  }}
                >
                  {off === 1 ? "See the 1 that is not" : `See the ${appCount(off)} that are not`}
                </a>
              )}
            </p>
          )}
        </div>
      )}
      {error && (
        <p className="lcr-settings-error" role="alert">
          {error}
        </p>
      )}
      <LateReplySettings workspaceId={workspace.id} />
    </section>
  );
}

/**
 * One campaign's switch, on its page. Only for a campaign free to draft: one held for its setup or
 * its copy is started by that hold's own ways out, and the page shows those instead, because a bare
 * "On" there would either be refused or skip the check the hold exists for.
 */
export function CampaignDrafting({ campaignId }: { readonly campaignId: string }) {
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [announced, setAnnounced] = useState("");
  const overview = useDraftingOverview(workspace.id);
  const campaign = overview.data?.campaigns.find((candidate) => candidate.id === campaignId);
  if (!overview.data || !campaign) return null;
  if (!overview.data.available)
    return (
      <div className="lcr-campaign-drafting">
        <p className="lcr-settings-note">{NOT_AVAILABLE}</p>
      </div>
    );
  const workspaceState = overview.data.state;
  const save = async (state: z.infer<typeof ScopedDraftingSchema>): Promise<void> => {
    setBusy(true);
    setError("");
    setAnnounced("");
    try {
      await flip(workspace.id, { level: "campaign", campaignId, state });
      await Promise.all([
        cache.invalidateQueries({ queryKey: draftingKey(workspace.id) }),
        cache.invalidateQueries({ queryKey: ["plan", workspace.id] }),
      ]);
      setAnnounced("Drafting switch saved.");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The switch did not change.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lcr-campaign-drafting">
      <Segmented
        label="Drafting for this campaign"
        value={campaign.state}
        options={scopedOptions(workspaceState, "workspace")}
        disabled={busy}
        onChange={(state) => void save(state)}
      />
      {campaign.heldBy === "workspace" && (
        <p className="lcr-settings-note">
          The whole workspace is off, so this campaign does not draft whatever it says here.
        </p>
      )}
      {error && (
        <p className="lcr-settings-error" role="alert">
          {error}
        </p>
      )}
      <span className="lcr-visually-hidden" role="status">
        {announced}
      </span>
    </div>
  );
}

const heldReason: Record<z.infer<typeof DraftingLevelSchema>, string> = {
  workspace: "the workspace is switched off",
  campaign: "the campaign is switched off",
  lead: "this lead is switched off",
};

function setBy(
  stored: NonNullable<z.infer<typeof StoredSchema>>,
  viewerId: string | null,
  when: (at: string) => string,
): string {
  const who =
    stored.updatedBy === `rep:${viewerId}`
      ? "you"
      : stored.updatedBy.startsWith("rep:")
        ? "a rep"
        : "setup";
  return `Set by ${who}, ${when(stored.updatedAt)}`;
}

/** Why this conversation drafts or not, and where to change it. */
function HeldSentence(props: {
  readonly heldBy: z.infer<typeof DraftingLevelSchema> | null;
  readonly workspaceName: string;
  readonly campaignLink: ReactNode | undefined;
}) {
  const { heldBy, campaignLink } = props;
  return (
    <p>
      {heldBy === null
        ? "New replies from this lead are drafted."
        : `New replies are shown and not drafted, because ${heldReason[heldBy]}.`}
      {heldBy === "workspace" && ` Change it in ${props.workspaceName} settings.`}
      {heldBy === "campaign" && campaignLink !== undefined && <> Change it on {campaignLink}.</>}
    </p>
  );
}

/** The lead's own switch. */
function LeadSwitch(props: {
  readonly state: z.infer<typeof ScopedDraftingSchema>;
  readonly inherited: "on" | "off";
  readonly busy: boolean;
  readonly onChange: (state: z.infer<typeof ScopedDraftingSchema>) => void;
}) {
  return (
    <Segmented
      label="Drafting for this lead"
      value={props.state}
      options={scopedOptions(props.inherited, "campaign")}
      disabled={props.busy}
      onChange={props.onChange}
    />
  );
}

/**
 * The conversation's one switch: its effective state, where that was decided, and the lead-level
 * choice. An inline disclosure rather than a dialog, so the thread stays in view.
 */
export function LeadDrafting({
  planId,
  decision,
  viewerId,
  when,
  campaignLink,
}: {
  planId: string;
  decision: DraftingDecision;
  viewerId: string | null;
  when: (at: string) => string;
  /** A link to this conversation's campaign page, where its own switch is. */
  campaignLink?: ReactNode;
}) {
  const workspace = useWorkspace();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  if (!decision) return null;
  const on = decision.heldBy === null;
  const campaignOn = decision.workspace.state === "on" && decision.campaign.state !== "off";
  const lead = decision.lead;
  const save = async (state: z.infer<typeof ScopedDraftingSchema>): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await flip(workspace.id, { level: "lead", planId, state });
      await Promise.all([
        cache.invalidateQueries({ queryKey: ["plan", workspace.id, planId] }),
        cache.invalidateQueries({ queryKey: ["plans", workspace.id] }),
        // The unified inbox's row states and agent-state list, which a held lead leaves.
        refreshAgentReads(cache, workspace.id),
      ]);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The switch did not change.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lcr-drafting-lead">
      <button
        type="button"
        className="lcr-drafting-toggle"
        data-on={on}
        aria-expanded={open}
        aria-controls={`drafting-${planId}`}
        onClick={() => setOpen(!open)}
      >
        <span className="lcr-drafting-dot" aria-hidden="true" />
        {/* Off stops new drafts and keeps one already made, so the label says "new" and never
            contradicts a Draft ready beside it (step 10, round 3). */}
        {on ? "Drafting on" : `New drafts off for this ${decision.heldBy}`}
      </button>
      {open && !decision.available && (
        <div className="lcr-drafting-panel" id={`drafting-${planId}`}>
          <p>{NOT_AVAILABLE}</p>
        </div>
      )}
      {open && decision.available && (
        <div className="lcr-drafting-panel" id={`drafting-${planId}`}>
          <HeldSentence
            heldBy={decision.heldBy}
            workspaceName={workspace.name}
            campaignLink={campaignLink}
          />
          {lead ? (
            <>
              <div className="lcr-drafting-row">
                <span className="lcr-drafting-name">This lead</span>
                <LeadSwitch
                  state={lead.state}
                  inherited={campaignOn ? "on" : "off"}
                  busy={busy}
                  onChange={(state) => void save(state)}
                />
              </div>
              {lead.state === "on" && !campaignOn && (
                <p className="lcr-drafting-note">
                  On here cannot override the {decision.heldBy} being off.
                </p>
              )}
              {lead.stored && (
                <p className="lcr-drafting-note">{setBy(lead.stored, viewerId, when)}</p>
              )}
            </>
          ) : (
            <p className="lcr-drafting-note">
              Bison named no lead for this thread, so only the campaign and workspace switches
              apply.
            </p>
          )}
          {error && <p role="alert">{error}</p>}
        </div>
      )}
    </div>
  );
}
